/**
 * The day's receipt, kept across reloads.
 *
 * A workout is not a page load. The athlete drops the phone, the screen locks,
 * the tab is reclaimed by iOS for memory, they come back after the rest between
 * sets and reload — and before this, that was a fresh ticket with the earlier
 * sets gone. So the sets are banked as they finish and the ticket is the day's,
 * not the session's.
 *
 * **The day boundary is local midnight**, taken from the device clock. Not a
 * rolling 24 hours, which would merge a late Monday into a Tuesday morning, and
 * not UTC, which would cut a European evening session in half. A workout that
 * genuinely crosses midnight is split, and that is the honest reading: it is two
 * dates.
 *
 * What is stored is the summary — counts and criterion tallies — and never the
 * samples or the landmarks. That line is the premise of the whole app: no video
 * and nothing derived from it leaves the machine, and keeping a day's figures
 * does not change that.
 *
 * The shape is versioned and plain, with the exercise referred to by id, so that
 * the planned backend can take the same records as a body without a migration:
 * what is written here is what would be posted.
 */
import { SetScore, Workout } from "./score";
import { readRenamed, safeStorage } from "./progress";

const KEY = "spottr.workout.v1";
/** The key before the rename from RoboCoach; see `readRenamed`. */
const OLD_KEY = "robocoach.workout.v1";

/** Local calendar day as YYYY-MM-DD. Local, for the reason above. */
export function dayKey(at = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

export class WorkoutStore {
  constructor(storage = safeStorage()) {
    this.storage = storage;
    this.day = dayKey();
  }

  /**
   * Today's workout, or an empty one.
   *
   * @param exerciseById Resolves a stored id back to its definition.
   * @return `{ workout, earned }` — the second being the per-set XP rows the
   *   receipt prints, in the same order, from when those sets were banked.
   */
  load(exerciseById) {
    const empty = { workout: new Workout(), earned: [] };
    if (this.storage == null) {
      return empty;
    }
    let stored;
    try {
      const raw = readRenamed(this.storage, KEY, OLD_KEY);
      stored = raw ? JSON.parse(raw) : null;
    } catch {
      return empty;
    }
    // A record from another day is yesterday's ticket, not this one's. It is
    // left in place rather than deleted: the next save overwrites it, and a
    // clock that has jumped backwards for an hour should not destroy a workout.
    if (stored == null || stored.day !== this.day) {
      return empty;
    }
    const sets = Array.isArray(stored.sets) ? stored.sets : [];
    const workout = new Workout();
    const earned = [];
    sets.forEach((record, i) => {
      const set = SetScore.fromJSON(
        record,
        exerciseById(record && record.exercise)
      );
      if (set == null) {
        return;
      }
      workout.add(set);
      const gain = Array.isArray(stored.earned) ? stored.earned[i] : null;
      earned.push(
        gain && typeof gain === "object"
          ? { gained: gain.gained || 0, from: gain.from, to: gain.to }
          : { gained: 0, from: null, to: null }
      );
    });
    return { workout, earned };
  }

  /** Banks the day's sets. Called as each one finishes. */
  save(workout, earned) {
    if (this.storage == null) {
      return;
    }
    try {
      this.storage.setItem(
        KEY,
        JSON.stringify({
          version: 1,
          day: this.day,
          sets: workout.sets.map((set) => set.toJSON()),
          earned,
        })
      );
    } catch {
      // Out of quota, or private mode. The ticket on screen is still right for
      // this session; it just will not survive the reload.
    }
  }

  /**
   * Whether the day has turned over since this store was made.
   *
   * The app is left open on a phone for hours, so the day it started on is not
   * necessarily the day a set ends on.
   */
  rolledOver() {
    return dayKey() !== this.day;
  }

  /** Moves to today, so the next save starts a new day's ticket. */
  startNewDay() {
    this.day = dayKey();
  }
}
